import React, {FC} from 'react'

import {detectPlatform} from '../../../helpers/other'
import {inputStyle} from './styles'
import {FieldProps} from './types'

const handlePicker = (e: React.MouseEvent<HTMLInputElement>) => {
  try {
    const target = e.currentTarget as HTMLInputElement & {showPicker?: () => void}
    if (typeof target.showPicker === 'function') {
      target.showPicker()
    }
  } catch (error) {
    // Ignore error if picker is already showing or not supported
  }
}

export const DateField: FC<FieldProps> = ({qId, value, error, onChange, theme}) => {
  return (
    <input
      type='date'
      value={(value as string) || ''}
      onChange={(e) => onChange(qId, e.target.value)}
      onClick={handlePicker}
      style={{...inputStyle(theme, !!error), cursor: 'pointer'}}
    />
  )
}

export const TimeField: FC<FieldProps> = ({qId, question, value, error, onChange, theme}) => {
  const isDesktop = detectPlatform() === 'other'

  const [localVal, setLocalVal] = React.useState((value as string) || '')
  React.useEffect(() => {
    setLocalVal((value as string) || '')
  }, [value])

  if (isDesktop) {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      let val = e.target.value.replace(/[^0-9:]/g, '')

      // Auto insert colon
      if (val.length >= 3 && !val.includes(':')) {
        val = `${val.slice(0, 2)}:${val.slice(2)}`
      } else if (val.length === 2 && !val.includes(':') && localVal.length !== 3) {
        val += ':'
      }

      if (val.length > 5) {
        val = val.slice(0, 5)
      }

      // Basic time validation
      if (val.length >= 3) {
        const [h, m] = val.split(':')
        if (parseInt(h || '0', 10) > 23) {
          val = `23:${m || ''}`
        }
        if (m && parseInt(m || '0', 10) > 59) {
          val = `${h}:59`
        }
      }

      setLocalVal(val)
      if (val.length === 5) {
        onChange(qId, val)
      } else if (val === '') {
        onChange(qId, '')
      }
    }

    const handleBlur = () => {
      if (localVal.length > 0 && localVal.length < 5) {
        setLocalVal('')
        onChange(qId, '')
      }
    }

    return (
      <input
        type='text'
        placeholder='HH:MM'
        value={localVal}
        onChange={handleChange}
        onBlur={handleBlur}
        style={{...inputStyle(theme, !!error), cursor: 'text'}}
        maxLength={5}
      />
    )
  }

  return (
    <input
      type='time'
      lang='en-GB'
      value={(value as string) || ''}
      onChange={(e) => onChange(qId, e.target.value)}
      onClick={handlePicker}
      style={{...inputStyle(theme, !!error), cursor: 'pointer'}}
    />
  )
}

export const DateTimeField: FC<FieldProps> = ({qId, question, value, error, onChange, theme}) => {
  const isDesktop = detectPlatform() === 'other'

  if (isDesktop) {
    const datePart = (value as string)?.split('T')[0] || ''
    const timePart = (value as string)?.split('T')[1] || ''

    const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!e.target.value) {
        onChange(qId, timePart ? `T${timePart}` : '')
      } else {
        onChange(qId, `${e.target.value}T${timePart || '00:00'}`)
      }
    }

    const handleTimeChange = (val: string) => {
      if (!val && !datePart) {
        onChange(qId, '')
      } else {
        onChange(qId, `${datePart || ''}T${val}`)
      }
    }

    return (
      <div style={{display: 'flex', gap: '8px'}}>
        <input
          type='date'
          value={datePart}
          onChange={handleDateChange}
          style={{...inputStyle(theme, !!error), cursor: 'pointer', flex: 1}}
        />
        <div style={{flex: 1}}>
          <TimeField qId={qId} question={question} value={timePart} error={error} onChange={(_, val) => handleTimeChange(val as string)} theme={theme} />
        </div>
      </div>
    )
  }

  return (
    <input
      type='datetime-local'
      lang='en-GB'
      value={(value as string) || ''}
      onChange={(e) => onChange(qId, e.target.value)}
      onClick={handlePicker}
      style={{...inputStyle(theme, !!error), cursor: 'pointer'}}
    />
  )
}
